import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { JobsContext } from 'libs.jobs.react';
import { Button, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { downloadFromSignedUrl } from '../utils/file_utils';
// Constants for notification messages
const NOTIFICATION_MESSAGES = {
    NETWORK_ERROR: 'Network error: please try again in a moment',
    EXPORT_STARTED: 'Export started',
    EXPORT_PROCESSING: "Your export is processing. We'll display a notification here once the download is ready.",
    EXPORT_SUCCESS: 'Your export file is available to download.',
    EXPORT_FAILED: 'Export failed',
    UNKNOWN_ERROR: 'An unknown error occurred',
    SUCCESS: 'Success',
    ERROR: 'Error',
};
// For end-to-end (e2e) tests, we automatically hide this notification after 5 seconds to prevent flakiness.
// TODO: Update e2e tests to consistently and safely close this notification themselves.
const NOTIFICATION_TIMEOUT = process.env.STORAGE_IMPLEMENTATION === 'firebase-emulator' ? 5000 : 0;
export const useLocalizationExportV2 = () => {
    const translate = useLocalizeMessage();
    const { post } = useApiClient(ApiClientService.WEB);
    const { addNotification } = useToastNotification();
    const { firebaseToken } = useContext(FirebaseAuthContext);
    const jobsContext = useContext(JobsContext);
    const handleExport = async ({ resources, scope, viewport, study, assets, productVersion, }) => {
        if (!firebaseToken) {
            throw new Error(translate(NOTIFICATION_MESSAGES.NETWORK_ERROR));
        }
        const response = await post('/api/v2/translation/export', { resources, scope, viewport, study, assets, ...(productVersion ? { productVersion } : {}) }, {
            withCredentials: false,
            headers: {
                Authorization: `Bearer ${firebaseToken}`,
            },
        });
        return response.data;
    };
    const handleJobCompletion = (completion_message) => {
        const signedUrl = completion_message?.params?.signedUrl;
        addNotification({
            title: translate(completion_message?.message_key ?? NOTIFICATION_MESSAGES.SUCCESS),
            type: 'success',
            timeout: NOTIFICATION_TIMEOUT,
            action: (_jsxs("div", { className: 'flex gap-2 items-center', children: [_jsx("div", { className: 'flex-1', children: translate(NOTIFICATION_MESSAGES.EXPORT_SUCCESS) }), _jsx(Button, { variant: 'transparent', label: translate('Download'), onClick: async (event) => {
                            event?.preventDefault?.();
                            try {
                                await downloadFromSignedUrl(signedUrl);
                            }
                            catch (e) {
                                console.error(e);
                            }
                        } })] })),
        });
    };
    const handleJobError = (error_message) => {
        addNotification({
            title: translate(error_message?.message_key ?? NOTIFICATION_MESSAGES.ERROR),
            type: 'error',
        });
    };
    return useMutation({
        mutationKey: ['translation', 'export'],
        mutationFn: handleExport,
        onSuccess: (data) => {
            addNotification({
                title: translate(NOTIFICATION_MESSAGES.EXPORT_STARTED),
                subtitle: translate(NOTIFICATION_MESSAGES.EXPORT_PROCESSING),
                type: 'info',
                timeout: NOTIFICATION_TIMEOUT,
            });
            if (data?.jobId && jobsContext) {
                jobsContext.addJobHandler(data?.jobId, {
                    onDone: handleJobCompletion,
                    onError: handleJobError,
                });
            }
        },
        onError: (error) => {
            addNotification({
                title: translate(NOTIFICATION_MESSAGES.EXPORT_FAILED),
                subtitle: error.message || translate(NOTIFICATION_MESSAGES.UNKNOWN_ERROR),
                type: 'error',
            });
        },
    });
};
